// Reconocer que devolvió una consulta, **por su forma y no por su nombre**.
//
// Atarlo al nombre de la herramienta —«si acaba en `_stats`, son estadísticas»—
// haría que una capacidad nueva no se pintara hasta que alguien la añadiera
// aquí a mano. Es el mismo error que el Brain evita leyendo el contrato en vez
// de una lista escrita: en `brain-agente-maestro` se comprobó añadiendo un
// endpoint de prueba y viendo que lo usaba sin tocar nada suyo.
//
// Vive aparte de los componentes porque no es uno: mezclarlos rompe el fast
// refresh, y la regla coincide con R5 —un componente por fichero—.

import type { MCPActionStats } from '@/types/tool'

// La forma de las estadisticas ya estaba declarada en `types/tool.ts` como
// `MCPActionStats`: aqui se reutiliza en vez de redeclararla. Se escribio dos
// veces por descuido el 2026-08-25 y lo caza la auditoria de simplicidad.
//
// Se relaja a `Partial` porque aqui llega desde una respuesta de herramienta y
// no de una llamada tipada: lo que el rastro trae puede venir incompleto.
export type Estadisticas = {
  executions?: Partial<MCPActionStats['executions']>
  performance?: Partial<MCPActionStats['performance']>
}

export interface Entidad {
  id?: string
  name?: string
  display_name?: string
  description?: string
  is_enabled?: boolean
  [clave: string]: unknown
}

/** Lo que devolvió la API, desenvuelto.
 *
 * El resultado llega en tres capas —`raw_data.data.data`—: la del envoltorio de
 * la herramienta, la de la respuesta HTTP y la del sobre `{success, data, meta}`
 * de `agent/v1`.
 */
export function cargaUtil(resultado: unknown): unknown {
  if (typeof resultado !== 'object' || resultado === null) return null
  const bruto = (resultado as Record<string, unknown>).raw_data
  if (typeof bruto !== 'object' || bruto === null) return null
  const respuesta = (bruto as Record<string, unknown>).data
  if (typeof respuesta !== 'object' || respuesta === null) return null
  return (respuesta as Record<string, unknown>).data ?? respuesta
}

export function pareceEstadisticas(datos: unknown): datos is Estadisticas {
  return (
    typeof datos === 'object' &&
    datos !== null &&
    'executions' in datos &&
    typeof (datos as Estadisticas).executions === 'object'
  )
}

export function pareceListaDeEntidades(
  datos: unknown,
): { entidades: Entidad[]; total?: number } | null {
  if (typeof datos !== 'object' || datos === null) return null
  const d = datos as Record<string, unknown>
  const lista = d.items ?? d.tools ?? d.agents
  if (!Array.isArray(lista)) return null
  const utiles = lista.filter(
    (e) => typeof e === 'object' && e !== null && ('name' in e || 'display_name' in e),
  )
  if (!utiles.length) return null
  return {
    entidades: utiles as Entidad[],
    total: typeof d.count === 'number' ? d.count : undefined,
  }
}
